/**
 * Dutch catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 1.
 *
 * `dir: 'ltr'`. Native-grade Dutch at a mathematician's register. **Warrant → „Rechtvaardiging"**
 * (the epistemic-justification reading; Toulmin's „garantie" is the flagged alternative). The
 * empirical kinds follow Dutch philosophy-of-science usage — toetsbaar / getoetst (Popper).
 *
 * W/M grades stay Latin (`::numbering-system/latn`) — a grade is a stable id, not prose.
 * CLDR cardinal categories for Dutch are one / other, so `frameActive` declares exactly those
 * (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a presentation concern
 * only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const NL_CATALOG = {
    locale: 'nl',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Onderzoek',
        [P2.sectionClaims]: 'Beweringen',
        [P2.sectionIntegrity]: 'Integriteit',
        [P2.axisWarrantLabel]: 'Rechtvaardiging',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirisch',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definitie} interp {Interpretatie} testable {Toetsbaar} tested {Getoetst} refuted {Weerlegd} program {Onderzoeksprogramma} philos {Filosofisch} other {Onbekend}}',
        [P2.axisModalLabel]: 'Modaal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Sterkteniveau {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Verkenbaar',
        [P2.stateRefuted]: 'Weerlegd',
        [P2.stateOutside]: 'Buiten bereik',
        [P2.verdictConflict]: 'In conflict met {constraints}',
        [P2.verdictOutside]: 'Vereist {missing}',
        [P2.lossRegisterPlain]: 'Vereenvoudigd tot gewone taal — een deel van de precisie vervalt.',
        [P2.lossRegisterKid]: 'Vereenvoudigd voor jongere lezers — veel details vervallen.',
        [P2.lossProfileDetail]: 'Het volledige profiel Rechtvaardiging / Empirisch / Modaal wordt hier samengevat.',
        [P2.lossConflictDetail]: 'De conflictdetails zijn in deze weergave ingekort.',
        [P2.laneVerified]: 'Geverifieerd',
        [P2.laneUnverified]: 'Niet geverifieerd',
        [P2.laneConflict]: 'Conflict bewezen',
        [P2.constraintLogic]: 'Logica',
        [P2.constraintLightBends]: 'Licht buigt af in een zwaartekrachtveld',
        [P2.constraintParallelPostulate]: 'Het parallellenpostulaat',
        [P2.claimScalarGravity]: 'Scalaire zwaartekrachttheorie',
        [P2.claimNonEuclidean]: 'Niet-euclidische meetkunde',
        [P2.claimSubstrateSr]: 'Substraatinterpretatie van de speciale relativiteitstheorie',
        [P2.edgeExtObservation]: 'Externe waarneming',
        [P2.edgeResourceSample]: 'Gedeelde rekenbron',
        [P2.edgeHeuristicSample]: 'Heuristische sturing',
        [P2.edgeSharedSample]: 'Gedeelde grondslag',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Beperkingen',
        [P2.sectionEdges]: 'Kanten',
        [P2.sectionFrame]: 'Kader',
        [P2.sectionExposure]: 'Blootstelling',
        [P2.statusAdoptedFormal]: 'Aangenomen (formeel)',
        [P2.statusAdoptedInterpretive]: 'Aangenomen (interpretatief)',
        [P2.statusSuspended]: 'Opgeschort',
        [P2.edgeEffectConstraint]: 'Vormt een beperking (kan weerleggen)',
        [P2.edgeEffectResource]: 'Bron — geen effect op oordelen',
        [P2.edgeEffectHeuristic]: 'Heuristiek — geen effect op oordelen',
        [P2.edgeEffectShared]: 'Gedeelde grondslag — geen effect op oordelen',
        [P2.frameActive]: '{count, plural, =0 {Geen actieve beperkingen} one {# actieve beperking} other {# actieve beperkingen}}',
        [P2.exposureNone]: 'Geen interpretatieve afhankelijkheden',
        [P2.exposureDep]: 'Hangt af van {id}',
        [P2.laneTrackMachine]: 'Machine',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Gemeenschap',
        [P2.expertAudited]: 'Geauditeerd',
        [P2.expertInProgress]: 'In uitvoering',
        [P2.expertNotReviewed]: 'Niet beoordeeld',
        [P2.communityFlags]: 'Meldingen aanwezig',
        [P2.communityNone]: 'Geen meldingen',
        [P2.controlChallenge]: 'Betwisten',
        [P2.controlActivate]: 'Activeren',
        [P2.controlMakeInterpretive]: 'Interpretatief maken',
        [P2.controlImportEdge]: 'Kant importeren',
        [P2.controlPromote]: 'Promoveren',
        [P2.controlUndo]: 'Ongedaan maken',
        [P2.controlRedo]: 'Opnieuw',
        [P2.controlRegister]: 'Registreren',
        [P2.promoteRejected]: 'Promotie afgewezen',
        [P2.promoteAccepted]: 'Promotie geaccepteerd',
        [P2.promoteReasonRefutationTerminal]: 'Een bewijs kan een weerlegging niet herstellen.',
        [P2.promoteReasonKeystone]: 'Een sluitsteenpromotie vereist empirische ondersteuning.',
        [P2.promoteReasonEmpiricalNotFixed]: 'De empirische soort is niet vastgelegd.',
        [P2.promoteReasonModalNotFixed]: 'De modale graad is niet vastgelegd.',
        [P2.promoteReasonWarrantNotImproved]: 'De rechtvaardigingsgraad is niet verbeterd.',
        [P2.evidenceComputation]: "{engine} berekende {op} = {result} onder {config}",
        [P2.lossNumericRounding]: "Resultaat afgerond op {dp} decimalen",
    },
};
//# sourceMappingURL=nl.js.map